<template>
  <div class="leadgen-forms-view flex flex-col">
    <page-header
      :title="tr('Lead generation forms')"
      :info="tr('Manage your ad-related Lead Gen forms in one place! Connect these to :app automations to ensure the selected form opens when your ad is clicked. Click on any item to view details and easily download the list of collected leads.')"
    />

    <div class="flex flex-col gap-4 py-5">
      <div v-if="facebookSourceProfiles.length !== 0" class="flex flex-wrap items-center gap-3">
        <brand-filter v-model="brandId" class="w-full sm:w-72" />
        <refresh-button
          v-if="selectedSourceProfileId && hasPermission"
          :label="tr('Update lead generation forms')"
          :success-message="tr('Lead generation forms updated successfully.')"
          :promise="() => refreshLeadgenForms()"
        />
      </div>

      <empty-screen
        v-if="facebookSourceProfiles.length === 0"
        icon="facebook"
        :title="tr('Connect a Facebook Page!')"
        :description="tr('We currently do not see a connected Facebook page, so we cannot display your forms. Click on the Integrations menu item and connect your page to the :app system!')"
      >
        <template #under-description>
          <ui-button as-child class="self-start">
            <router-link to="/integrations/meta">{{ tr('Connect a Facebook Page') }}</router-link>
          </ui-button>
        </template>
      </empty-screen>
      <empty-screen
        v-else-if="!hasPermission"
        icon="warning"
        :title="tr('No permission to view leadgen forms')"
        :flag-text="tr('Missing permission')"
        :description="tr('You do not have permission to view lead generation forms. To view lead generation forms, you need to reconnect the Facebook profile that is connected to the selected source.')"
      >
        <template #under-description>
          <ui-button as-child class="self-start">
            <router-link to="/integrations/meta">{{ tr('Reconnect Facebook Profile') }}</router-link>
          </ui-button>
        </template>
      </empty-screen>
      <empty-screen
        v-else-if="!isLoading && isListEmpty"
        icon="sad"
        :title="tr('No forms available')"
        :flag-text="tr('No forms')"
        :description="tr('There are currently no lead generation forms available in the selected view. Create one on the Meta interface for the relevant Facebook page, and it will automatically appear here in the list.')"
      >
        <template #under-description>
          <ui-button as-child class="self-start">
            <a :href="newFormLink" target="_blank">{{ tr('Create a New Form') }}</a>
          </ui-button>
        </template>
      </empty-screen>

      <div
        v-else
        role="table"
        :aria-label="tr('Lead generation forms')"
        class="flex flex-col gap-2"
      >
        <div role="row" class="grid items-center gap-x-4 max-md:grid-cols-[minmax(0,1fr)_auto] max-md:gap-y-2 md:grid-cols-[17rem_minmax(0,1fr)_10rem_7rem_2rem] px-4 text-xs font-semibold text-muted-foreground max-md:hidden">
          <span role="columnheader">{{ tr('Source') }}</span>
          <span role="columnheader">{{ tr('Form Name') }}</span>
          <span role="columnheader">{{ tr('Form ID') }}</span>
          <span role="columnheader" class="text-right">{{ tr('Leads Count') }}</span>
          <span role="columnheader" />
        </div>

        <div
          v-for="form in leadgenForms"
          :key="form.id"
          role="row"
          class="grid items-center gap-x-4 max-md:grid-cols-[minmax(0,1fr)_auto] max-md:gap-y-2 md:grid-cols-[17rem_minmax(0,1fr)_10rem_7rem_2rem] glass-tile rounded-xl p-4 md:py-2.5 cursor-pointer transition-shadow duration-300 ease-in-out hover:[--tile-glow:color-mix(in_oklab,var(--primary)_40%,transparent)]"
          @click="() => openForm(form)"
        >
          <div role="cell" class="max-md:col-span-2">
            <social-page :source-profiles="getGroupedSourceProfiles(form.source_profile_id)" />
          </div>
          <div role="cell" class="min-w-0 text-sm font-medium text-foreground max-md:col-span-2">{{ form.name }}</div>
          <div role="cell" class="truncate text-xs text-muted-foreground tabular-nums">{{ form.external_id }}</div>
          <div role="cell" class="text-sm font-semibold text-foreground tabular-nums md:text-right">
            {{ formatNumber(form.leads_count) }}
            <span class="font-normal text-muted-foreground md:hidden">{{ tr('Leads Count') }}</span>
          </div>
          <div role="cell" class="max-md:hidden">
            <arrow-link :label="form.name" @click.stop="() => openForm(form)" />
          </div>
        </div>

        <template v-if="hasMore || isLoading">
          <div
            v-for="i in [ 1, 2, 3 ]"
            :key="'leadgen-loader-skeleton--' + i"
            ref="scrollPagerTrigger"
            role="row"
            class="grid items-center gap-x-4 max-md:grid-cols-[minmax(0,1fr)_auto] max-md:gap-y-2 md:grid-cols-[17rem_minmax(0,1fr)_10rem_7rem_2rem] glass-tile rounded-xl p-4 md:py-2.5"
          >
            <div class="flex items-center gap-2 max-md:col-span-2">
              <ui-skeleton class="size-9 rounded-full" />
              <ui-skeleton class="h-4 w-24" />
            </div>
            <ui-skeleton class="h-4 w-48 max-md:col-span-2" />
            <ui-skeleton class="h-3.5 w-36" />
            <ui-skeleton class="h-4 w-10 md:justify-self-end" />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import ScrollPagerMixin from '@/mixins/ScrollPager.js';
import LeadgenFormService from '@/services/LeadgenForm.js';
import SourceProfileService from '@/services/SourceProfile.js';
import ArrowLink from '@/ui/components/ArrowLink.vue';
import BrandFilter from '@/ui/components/BrandFilter.vue';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import PageHeader from '@/ui/components/PageHeader.vue';
import RefreshButton from '@/ui/components/RefreshButton.vue';
import SocialPage from '@/ui/components/SocialPage.vue';
import { formatNumber } from '@/utils/number';

const ITEMS_PER_PAGE = 20;

export default {
  name: 'LeadgenFormsView',
  components: {
    ArrowLink,
    BrandFilter,
    EmptyScreen,
    RefreshButton,
    SocialPage,
    PageHeader,
  },
  mixins: [ ScrollPagerMixin ],
  data() {
    return {
      isLoading: false,
      hasMore: true,
      page: 0,
      leadgenForms: [],
      brandId: null,
    };
  },
  computed: {
    sourceProfiles() {
      return this.$root.currentCompany?.source_profiles ?? [];
    },
    facebookSourceProfiles() {
      return this.sourceProfiles.filter(sp => sp.platform === 'facebook') ?? [];
    },
    selectedSourceProfile() {
      if (!this.brandId) {
        return null;
      }

      return this.facebookSourceProfiles.find(sp => sp.brand_id === this.brandId);
    },
    selectedSourceProfileId() {
      return this.selectedSourceProfile?.id;
    },
    hasPermission() {
      if (!this.selectedSourceProfile) {
        if (this.facebookSourceProfiles.length === 1) {
          const sp = this.facebookSourceProfiles[0];
          return !!sp?.id && !sp.lost_access;
        }

        return true;
      }

      return !this.selectedSourceProfile.lost_access;
    },
    isListEmpty() {
      return this.leadgenForms.length === 0;
    },
    newFormLink() {
      const profile = this.selectedSourceProfile ?? this.facebookSourceProfiles[0];
      const profileId = profile.details?.facebook_page_id ?? profile.external_id;

      return `https://business.facebook.com/latest/instant_forms/forms/?asset_id=${profileId}`;
    },
  },
  watch: {
    brandId: {
      immediate: true,
      handler() {
        this.resetAndLoad();
      },
    },
  },
  methods: {
    formatNumber,
    resetAndLoad() {
      this.leadgenForms = [];
      this.page = 0;
      this.hasMore = true;

      if (!this.hasPermission) {
        this.hasMore = false;
        return;
      }

      this.loadNewData();
    },
    loadNewData() {
      if (this.isLoading || !this.hasMore) {
        return;
      }

      this.isLoading = true;
      ++this.page;

      const sourceProfileFilter = [];
      if (this.selectedSourceProfileId) {
        sourceProfileFilter.push(this.selectedSourceProfileId);
      }

      LeadgenFormService.getList({
        page: this.page,
        limit: ITEMS_PER_PAGE,
        sourceProfileIds: sourceProfileFilter,
      })
        .then(forms => {
          this.leadgenForms.push(...forms);
          this.hasMore = forms.length === ITEMS_PER_PAGE;

          return forms;
        })
        .catch(err => {
          this.hasMore = false;
          this.error(err);
        })
        .finally(() => {
          this.isLoading = false;
          if (this.hasMore) {
            this.$nextTick(() => this.initScrollPager('scrollPagerTrigger', 'loadNewData'));
          }
        });
    },
    openForm(leadgenForm) {
      this.$router.push('/leadgen-forms/' + leadgenForm.id);
    },
    getSourceProfile(id) {
      return this.facebookSourceProfiles.find(sp => sp.id === id);
    },
    getGroupedSourceProfiles(sourceProfileId) {
      const group = [];

      const primarySourceProfile = this.getSourceProfile(sourceProfileId);
      if (primarySourceProfile) {
        group.push(primarySourceProfile);
      }

      const secondaryProfiles = this.sourceProfiles.filter(sp => sp.usable_with_this === sourceProfileId);
      if (secondaryProfiles) {
        group.push(...secondaryProfiles);
      }

      return group;
    },
    refreshLeadgenForms() {
      return SourceProfileService.refreshLeadgenForms(this.selectedSourceProfileId).then(() => this.resetAndLoad());
    },
  },
};
</script>
